import { useEffect, useContext, useMemo, useCallback, useState, ReactNode, useId } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { ShellContext } from "../context";
import { useOwnerModule } from "../hooks";

/** Content subscription component properties. */
export interface ContentSubscriptionProps {

  /** The ID of the module containing the channel. */
  readonly moduleId: string | null;

  /** The ID of the channel. */
  readonly channelId: string;

  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;

  /** The optional subscription parameters. */
  readonly params?: unknown;

  /** The component children. */
  readonly children?: ReactNode;
}

const Container = styled.div`
  display: contents;
`;

/** A component that dynamically inserts its content into the specified content channel. Perfect for page-specific content such as menu bars. */
export const ContentSubscription = (props: ContentSubscriptionProps): ReactNode => {
  const { moduleId, channelId, children, params } = props;

  const { registerDynamicChannelSubscription } = useContext(ShellContext);
  const ownerModule = useOwnerModule();
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  const selector = moduleId === null ? `_c_${channelId}` : `_c_${moduleId}_${channelId}`;

  const refHandler = useCallback((ref: HTMLDivElement | null) => {
    setElement(ref);
  }, []);

  const component = useMemo(() => {
    const ContentSubscriptionContainer = () => (
      <Container className={selector} ref={refHandler} />
    );
    return ContentSubscriptionContainer;
  }, [selector]);

  useEffect(() => {
    if (!ownerModule) {
      console.warn("Content Subscription can only be used from within a module.");
      return;
    }

    const deregister = registerDynamicChannelSubscription(moduleId, channelId, {
      module: ownerModule,
      type: "component",
      priority: 0,
      component,
      params,
    });

    return (): void => {
      deregister();
    };
  }, [moduleId, channelId, component, params, registerDynamicChannelSubscription]);

  if (!element) return null;

  return (
    <>
      <ContentSubscriptionPortal container={element}>
        {children}
      </ContentSubscriptionPortal>
    </>
  );
};


/** The `ContentSubscriptionPortal` component properties. */
interface ContentSubscriptionPortalProps {
  
  /** The reference to the HTML element to use as the container for the subscription instance. */
  readonly container: Element | null;

  /** The component children. */
  readonly children?: ReactNode;
}

/** Injects children into a specified container HTML element if exists. */
const ContentSubscriptionPortal = (props: ContentSubscriptionPortalProps): ReactNode => {
  const { container, children } = props;

  if (!container) return null;

  let renderChildren = children;

  // Compatibility with older calling mechanism.
  if (typeof children === "function") {
    // @ts-ignore
    renderChildren = children();
  }

  return createPortal(renderChildren, container);
};


/** The `ActionBar` component properties. */
export interface ActionBarProps {

  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;

  /** The component children. */
  readonly children?: ReactNode;
}

/** Subscribes a component to an action bar. */
export const ActionBar = (props: ActionBarProps): ReactNode => {
  const { children } = props;

  return (
    <ContentSubscription moduleId={null} channelId="app-action-bars">
      {children}
    </ContentSubscription>
  );
};

/** The `LeftSideBar` component properties. */
export interface LeftSideBarProps {

  /** The sidebar ID. If specified the closed/open state of the sidebar is tracked. */
  readonly id?: string;

  /** The component children. */
  readonly children?: ReactNode;

  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;
}

/** Subscribes a component to a left side bar. */
export const LeftSideBar = (props: LeftSideBarProps): ReactNode => {
  const { id, children } = props;

  const sidebarParams = useMemo(() => {
    return { id };
  }, [id]);

  return (
    <ContentSubscription moduleId={null} channelId="app-sidebar-left" params={sidebarParams}>
      {children}
    </ContentSubscription>
  );
};

/** The `RightSideBar` component properties. */
export interface RightSideBarProps {

  /** The sidebar ID. If specified the closed/open state of the sidebar is tracked. */
  readonly id?: string;

  /** The component children. */
  readonly children?: ReactNode;

  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;
}

/** Subscribes a component to a right side bar. */
export const RightSideBar = (props: RightSideBarProps): ReactNode => {
  const { id, children } = props;

  const sidebarParams = useMemo(() => {
    return { id };
  }, [id]);

  return (
    <ContentSubscription moduleId={null} channelId="app-sidebar-right" params={sidebarParams}>
      {children}
    </ContentSubscription>
  );
};

/** The `Task` component properties. */
export interface TaskProps {

  /** The task title. */
  readonly title: ReactNode;

  /** The function to call to close the task. */
  readonly onClose?: () => void;

  /** The component children. */
  readonly children?: ReactNode;
}

/** Registers a task which can be displayed within the Task flyout. */
export const Task = (props: TaskProps): ReactNode => {
  const { title, onClose, children } = props;

  const id = useId();

  const params = useMemo(() => {
    return {
      id,
      title,
      onClose,
    };
  }, [id, title, onClose]);

  return (
    <ContentSubscription moduleId={null} channelId="flyout-tasks" params={params}>
      {children}
    </ContentSubscription>
  );
}
